import { type ComponentType, useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from 'tss-react/mui';
import Tooltip from '@mui/material/Tooltip';
import { SvgIconProps } from '@mui/material/SvgIcon';
import GroupIcon from '@mui/icons-material/Group';
import CategoryIcon from '@mui/icons-material/Category';
import Person4OutlinedIcon from '@mui/icons-material/Person4Outlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import WebhookOutlinedIcon from '@mui/icons-material/WebhookOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import LocalLibraryOutlinedIcon from '@mui/icons-material/LocalLibraryOutlined';
import { getColors, typography } from '../tokens';
import {
  DomainIcon,
  BoxesIcon,
  ApartmentIcon,
  CogWheelIcon,
  StarOutlineIcon,
  StarFilledIcon,
} from '../icons';

const CARD_HEIGHT = 280;

export interface EntityCardProps {
  name: string;
  description?: string;
  kind: string;
  type?: string;
  category?: string;
  project?: string;
  tags?: string[];
  onClick?: (event?: React.MouseEvent<HTMLDivElement>) => void;
  starred?: boolean;
  onToggleStar?: () => void;
  onProjectClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  unavailable?: boolean;
}

const KIND_ICON_MAP: Record<string, ComponentType<SvgIconProps>> = {
  component: Person4OutlinedIcon,
  api: WebhookOutlinedIcon,
  system: DomainIcon,
  resource: BoxesIcon,
  infrastructure: ApartmentIcon,
  repository: FolderCopyOutlinedIcon,
  user: PersonOutlineOutlinedIcon,
  group: GroupOutlinedIcon,
};

const TYPE_ICON_MAP: Record<string, ComponentType<SvgIconProps>> = {
  service: DnsOutlinedIcon,
  datastore: DatasetOutlinedIcon,
  library: LocalLibraryOutlinedIcon,
  website: DesktopMacOutlinedIcon,
  storage: Inventory2OutlinedIcon,
  documentation: ArticleOutlinedIcon,
};

/**
 * Returns the appropriate icon for an entity based on its kind and type.
 *
 * This icon mapping was defined together with the design team. Currently we
 * check both kind and type because the data structure uses them inconsistently.
 * Once the data structure is improved and the left filter sidebar is added,
 * icons can/should be revisited to use a single source field.
 */
const getEntityIcon = (
  kind: string,
  type?: string,
): ComponentType<SvgIconProps> => {
  if (type) {
    const typeIcon = TYPE_ICON_MAP[type.toLowerCase()];
    if (typeIcon) return typeIcon;
  }
  return KIND_ICON_MAP[kind.toLowerCase()] || ScatterPlotIcon;
};

const useStyles = makeStyles<{ unavailable?: boolean }>()((
  theme,
  { unavailable },
) => {
  const colors = getColors(theme.palette.mode);
  const c = colors;
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  const iconColor = unavailable ? c.icon.disabled : c.primary.main;
  return {
    root: {
      background: unavailable ? c.cards.disabled : c.cards.background,
      border: `1px solid ${c.border.default}`,
      borderRadius: 12,
      padding: 20,
      cursor: 'pointer',
      minHeight: CARD_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0, 120, 214, 0.15)',
      },
    },
    starButton: {
      position: 'absolute',
      top: 16,
      right: 16,
      background: 'none',
      border: 'none',
      padding: 4,
      cursor: 'pointer',
      color: fgAlpha(0.3),
      zIndex: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      transition: 'color 0.15s ease, background 0.15s ease',
      '&:hover': {
        color: '#FFD700',
        background: fgAlpha(0.08),
      },
      '& svg': {
        width: 20,
        height: 20,
      },
    },
    starButtonActive: {
      color: '#FFD700',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 8,
      paddingRight: 28,
    },
    icon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: iconColor,
      '& svg': {
        width: 28,
        height: 28,
        '& path': {
          fill: iconColor,
        },
      },
    },
    titleBlock: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    name: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontWeight: 700,
      fontSize: '0.95rem',
      color: c.text.primary,
      margin: 0,
      lineHeight: 1.3,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    kind: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.75rem',
      color: c.text.secondary,
      lineHeight: 1.3,
    },
    description: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: colors.text.secondary,
      fontSize: '0.85rem',
      marginTop: 10,
      lineHeight: 1.45,
      minHeight: '2.9em',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    metaRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      marginTop: 12,
      flexWrap: 'wrap',
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.75rem',
      color: c.text.secondary,
      '& svg': {
        width: 14,
        height: 14,
        color: c.text.secondary,
      },
    },
    projectLink: {
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    tags: {
      display: 'flex',
      flexWrap: 'nowrap',
      gap: 6,
      marginTop: 'auto',
      paddingTop: 12,
      overflow: 'hidden',
      alignItems: 'center',
    },
    tag: {
      background: unavailable ? c.tags.disabled : c.tags.background,
      color: isLight ? c.text.secondary : c.text.primary,
      borderRadius: 14,
      border: 'none',
      padding: '3px 10px',
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.72rem',
      lineHeight: 1.4,
      whiteSpace: 'nowrap' as const,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexShrink: 0,
    },
    tagOverflow: {
      background: unavailable ? c.tags.disabled : c.tags.background,
      color: isLight ? c.text.secondary : c.text.primary,
      borderRadius: 14,
      padding: '3px 10px',
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.72rem',
      lineHeight: 1.4,
      whiteSpace: 'nowrap' as const,
      flexShrink: 0,
      cursor: 'default',
    },
  };
});

export const EntityCard = ({
  name,
  description,
  kind,
  type,
  category,
  project,
  tags,
  onClick,
  starred,
  onToggleStar,
  onProjectClick,
  className,
  style,
  unavailable,
}: Readonly<EntityCardProps>) => {
  const { classes } = useStyles({ unavailable });
  const Icon = getEntityIcon(kind, type);
  const kindLabel = type || kind;

  const tagsRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tags?.length ?? 0);

  useEffect(() => {
    const container = tagsRef.current;
    if (!container || !tags?.length) return;

    const children = Array.from(container.children) as HTMLElement[];
    const containerRight = container.getBoundingClientRect().right;
    let count = 0;

    for (const element of children) {
      const child = element;
      if (child.dataset.overflow) continue;
      const childRight = child.getBoundingClientRect().right;
      if (childRight > containerRight) break;
      count++;
    }

    setVisibleCount(count < tags.length ? Math.max(count - 1, 1) : tags.length);
  }, [tags]);

  const hasOverflow = tags && visibleCount < tags.length;
  const displayedTags = tags?.slice(0, hasOverflow ? visibleCount : undefined);

  return (
    <div
      className={[classes.root, className].filter(Boolean).join(' ')}
      style={style}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={e => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {onToggleStar && (
        <IconButton
          className={[classes.starButton, starred && classes.starButtonActive]
            .filter(Boolean)
            .join(' ')}
          onClick={e => {
            e.stopPropagation();
            onToggleStar();
          }}
          aria-label={starred ? 'Remove from favorites' : 'Add to favorites'}
          size="small"
          disableRipple
        >
          {starred ? <StarFilledIcon /> : <StarOutlineIcon />}
        </IconButton>
      )}
      <div className={classes.header}>
        <span className={classes.icon}>
          <Icon />
        </span>
        <div className={classes.titleBlock}>
          <h3 className={classes.name}>{name}</h3>
          <span className={classes.kind}>{kindLabel}</span>
        </div>
      </div>
      <p className={classes.description}>{description || ' '}</p>
      <div className={classes.metaRow}>
        <span className={classes.metaItem}>
          {category ? <CategoryIcon /> : <CogWheelIcon />}
          {category || kindLabel}
        </span>
        {project && (
          <span
            className={`${classes.metaItem} ${onProjectClick ? classes.projectLink : ''}`}
            role={onProjectClick ? 'link' : undefined}
            tabIndex={onProjectClick ? 0 : undefined}
            onClick={
              onProjectClick
                ? e => {
                    e.stopPropagation();
                    onProjectClick();
                  }
                : undefined
            }
            onKeyDown={
              onProjectClick
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      onProjectClick();
                    }
                  }
                : undefined
            }
          >
            <GroupIcon />
            {project}
          </span>
        )}
      </div>
      {tags && tags.length > 0 && (
        <div className={classes.tags} ref={tagsRef}>
          {displayedTags?.map(tag => (
            <span key={tag} className={classes.tag}>
              {tag}
            </span>
          ))}
          {hasOverflow && (
            <Tooltip
              title={tags.slice(visibleCount).join(', ')}
              arrow
              placement="top"
            >
              <span className={classes.tagOverflow} data-overflow="true">
                +{tags.length - visibleCount}
              </span>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};
